import {
  AppUpdateForm,
  type AppUpdateConfigRow,
} from "@/components/app-update/app-update-form";
import { AppUpdateHistory } from "@/components/app-update/app-update-history";
import { useLoaderData } from "react-router";
import { callServer } from "../api/server";

const PLATFORMS = ["android", "ios"] as const;

function defaultsFor(platform: string): AppUpdateConfigRow {
  return {
    platform,
    minVersion: "1.0.0",
    latestVersion: "1.0.0",
    blockedVersions: [],
    forceMessage: "",
    softMessage: "",
    storeUrl: "",
    logoutOnForce: false,
    updatedAt: new Date(),
    updatedBy: null,
  };
}

// The server owns the rules. If it can't be reached, show the defaults —
// the form still works and saving surfaces the real error.
export async function loader(): Promise<AppUpdateConfigRow[]> {
  const result = await callServer<AppUpdateConfigRow[]>("GET", `/api/v1/admin/app-version`);
  return result.ok ? result.data : [];
}

export default function AppUpdatePage() {
  const rows = useLoaderData() as AppUpdateConfigRow[];
  const byPlatform = new Map(rows.map(r => [r.platform, r]));

  const configs: AppUpdateConfigRow[] = PLATFORMS.map(p => {
    const raw = byPlatform.get(p);
    if (!raw) return defaultsFor(p);
    return {
      platform: raw.platform,
      minVersion: raw.minVersion,
      latestVersion: raw.latestVersion,
      blockedVersions: raw.blockedVersions,
      forceMessage: raw.forceMessage,
      softMessage: raw.softMessage,
      storeUrl: raw.storeUrl,
      logoutOnForce: raw.logoutOnForce,
      updatedAt: raw.updatedAt,
      updatedBy: raw.updatedBy,
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-[22px] font-bold text-white tracking-tight">
          App update
        </h1>
        <p className="text-zinc-500 text-[13px] mt-0.5">
          Per-platform min/latest versions, blocked list, force + soft
          messages. Saves snapshot to AppUpdateConfigHistory for rollback.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {configs.map(cfg => (
          <AppUpdateForm key={cfg.platform} initial={cfg} />
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <AppUpdateHistory platform="android" />
        <AppUpdateHistory platform="ios" />
      </div>
    </div>
  );
}
